import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { CroutonIds } from "@swiggy-private/cart-types";
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS, Storage } from "@swiggy-private/core-ui";
import {
    AnalyticsContext,
    CartOverlaysContext,
    SessionVisibility,
    useLocalStorageBasedFrequencyCapping,
} from "@swiggy-private/checkout-core";
import { CartContext } from "@cart-client/contexts/CartContext";
import {
    DELIVERY_MODE_ANIMATION_ASSET_ID,
    DELIVERY_MODE_ANIMATION_VISIBILITY_THRESHOLD,
} from "@cart-client/constants/CartDeliverySpeed";
import { useToingDeliveryModesAnalytics } from "@cart-client/hooks/useToingDeliveryModesAnalytics";
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import { shouldStartDeliveryModeAnimation } from "./helpers";

export const DELIVERY_MODE_ANIMATION_URL = getCloudinaryUrl(DELIVERY_MODE_ANIMATION_ASSET_ID) || "";

export interface UseDeliveryModeLottieReturnType {
    observerOptions: IntersectionObserverInit;
    shouldPlayAnimation: boolean;
    onAnimationComplete: VoidFunction;
    onDeliverySectionEnter: VoidFunction;
}

// Resets on full page reload (menu return / cart refresh),
// persists across SPA route changes (payment/coupon ↔ cart).
let playedInThisLoad = false;

const useDeliveryModeLottie = (): UseDeliveryModeLottieReturnType => {
    const { enabledCroutonId, onCloseCroutonImmediate } = useContext(CartOverlaysContext);
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { impressionFtueDeliveryModeAnimation } = useToingDeliveryModesAnalytics();

    const [shouldPlayAnimation, setShouldPlayAnimation] = useState(false);
    const isAnimatingRef = useRef(false);

    // Already played this checkout session (SPA nav OR same-tab refresh).
    const hasPlayedRef = useRef(
        playedInThisLoad || Storage.getItem<boolean>(SESSION_STORAGE_KEYS.DELIVERY_MODE_ANIMATION_SEEN, false),
    );

    const ftueData = cartResponse?.data?.toing_presentational_config?.delivery_modes_ftue_data;
    const maxFreq = ftueData?.animation_max_count;

    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: LOCAL_STORAGE_KEYS.DELIVERY_MODE_ANIMATION_FREQUENCY_CAPPING_KEY,
        sessionVisibility: SessionVisibility.EVERYTIME_PER_SESSION,
        maxFrequency: maxFreq,
    });

    // ONE ref with the latest values, so the observer callback stays stable (no re-fire).
    const latest = useRef({
        enabledCroutonId,
        onCloseCroutonImmediate,
        ftueData,
        maxFreq,
        shouldProceed,
        impressionEvent,
        impressionFtueDeliveryModeAnimation,
    });
    latest.current = {
        enabledCroutonId,
        onCloseCroutonImmediate,
        ftueData,
        maxFreq,
        shouldProceed,
        impressionEvent,
        impressionFtueDeliveryModeAnimation,
    };

    const observerOptions = useMemo<IntersectionObserverInit>(
        () => ({ threshold: DELIVERY_MODE_ANIMATION_VISIBILITY_THRESHOLD }),
        [],
    );

    const onAnimationComplete = useCallback(() => {
        if (!isAnimatingRef.current) return;
        isAnimatingRef.current = false;
        setShouldPlayAnimation(false);
    }, []);

    const onDeliverySectionEnter = useCallback(() => {
        const {
            enabledCroutonId,
            onCloseCroutonImmediate,
            ftueData,
            maxFreq,
            shouldProceed,
            impressionEvent,
            impressionFtueDeliveryModeAnimation,
        } = latest.current;

        if (enabledCroutonId === CroutonIds.DELIVERY_SPEED_TOING) onCloseCroutonImmediate();

        if (
            !shouldStartDeliveryModeAnimation({
                hasPlayed: Boolean(hasPlayedRef.current),
                isAnimating: isAnimatingRef.current,
                hasFtueData: Boolean(ftueData),
                maxFreq,
                shouldProceed,
            })
        )
            return;

        hasPlayedRef.current = true;
        playedInThisLoad = true;
        Storage.setItem(SESSION_STORAGE_KEYS.DELIVERY_MODE_ANIMATION_SEEN, true);
        isAnimatingRef.current = true;
        setShouldPlayAnimation(true);
        impressionFtueDeliveryModeAnimation(impressionEvent);
    }, []);

    return {
        observerOptions,
        shouldPlayAnimation,
        onAnimationComplete,
        onDeliverySectionEnter,
    };
};

export default useDeliveryModeLottie;
